/** API client. Same-origin /api/*, cookie session. */

export interface Account { id: string; email: string; createdAt: number; ageOk: boolean; admin: boolean }
export interface Era { id: string; ordinal: number; status: string; started_at: number | null; ended_at: number | null }
export interface Profile { id: string; era_id: string; handle: string; created_at: number; archived_at: number | null; renamed: number }
export interface Me { account: Account | null; era?: Era; profile?: Profile | null; history?: Profile[] }
export interface Friend { account_id: string; handle: string | null; status: "pending_in" | "pending_out" | "accepted"; since: number }
export interface NewsItem { kind: "post" | "release"; title: string; date: string; version?: string; pinned?: boolean; html: string }

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body ? { "content-type": "application/json" } : {},
    body: body ? JSON.stringify(body) : null,
    credentials: "same-origin",
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) throw new ApiError(res.status, data.error ?? `HTTP ${res.status}`);
  return data;
}

export interface Skill { id: string; name: string; xp: number; level: number }
export interface DirFile { id: string; owner: string; floorIndex: number; depth: number; killedBy: string | null; hops: number; location: string; contents: { augments: string[] }; from: string | null }
export interface Directory { held: DirFile[]; returned: DirFile[]; stored: DirFile[]; slots: { used: number; max: number }; feed: { from: string; at: number }[] }

export const api = {
  version: () => call<{ build: string; channel: string }>("GET", "/api/version"),
  me: () => call<Me>("GET", "/api/me"),
  requestCode: (email: string) => call<{ ok: true; devCode?: string }>("POST", "/api/auth/code", { email }),
  verify: (email: string, code: string, device: string) => call<{ ok: true; isNew: boolean; account: Account }>("POST", "/api/auth/verify", { email, code, device }),
  logout: () => call<{ ok: true }>("POST", "/api/auth/logout", {}),
  ageOk: () => call<{ ok: true }>("POST", "/api/me/age", {}),
  sessions: () => call<{ sessions: { id: string; device: string; lastSeen: number; current: boolean }[] }>("GET", "/api/me/sessions"),
  revoke: (id: string) => call<{ ok: true }>("DELETE", `/api/me/sessions/${id}`),
  createProfile: (handle: string) => call<{ profile: Profile }>("POST", "/api/profile", { handle }),
  rename: (handle: string) => call<{ profile: Profile }>("POST", "/api/profile/rename", { handle }),
  profile: (handle: string) => call<{ profile: { handle: string; era: string; since: number }; skills: Skill[]; floors: Record<string, number>; bosses?: { boss: string; name: string; kills: number; firstAt: number; bestTicks: number }[]; hiscores: unknown[]; collection: unknown[] }>("GET", `/api/profiles/${encodeURIComponent(handle)}`),
  skills: () => call<{ skills: Skill[]; floors: Record<string, number> }>("GET", "/api/me/skills"),
  directoryPeek: () => call<Directory>("GET", "/api/directory?peek=1"),
  currentRun: () => call<{ floor: { runId: string; floorIndex: number; members?: unknown[] } | null }>("GET", "/api/runs/current"),
  board: () => call<Board>("GET", "/api/board"),
  resources: () => call<{ resources: { id: string; name: string; amount: number }[] }>("GET", "/api/me/skills"),
  hiscores: () => call<{ boards: unknown[] }>("GET", "/api/hiscores"),
  friends: () => call<{ friends: Friend[] }>("GET", "/api/friends"),
  friendRequest: (handle: string) => call<{ ok: true }>("POST", "/api/friends/request", { handle }),
  friendAccept: (accountId: string) => call<{ ok: true }>("POST", "/api/friends/accept", { accountId }),
  friendRemove: (accountId: string) => call<{ ok: true }>("POST", "/api/friends/remove", { accountId }),
  news: async (): Promise<NewsItem[]> => {
    const res = await fetch("/news.json", { cache: "no-cache" });
    return res.ok ? ((await res.json()) as NewsItem[]) : [];
  },
};

/** The sector board (design §7.3): pooled goals and the overloads in effect. */
export interface Board {
  sector: string; now: number;
  goals: { id: string; name: string; description: string; source: string; target: number; progress: number; cycle: number; mine: number; reopensAt: number | null; grants: { name: string; durationMinutes: number } }[];
  overloads: { id: string; name: string; description: string; endsAt: number }[];
  caches: { item: string; floorIndex: number; location: string }[];
}
